import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiEdit, FiPlus } from 'react-icons/fi';
import ConditionalButton from '../components/common/ConditionalButton';
import Modal from '../components/common/Modal';
import { getUploadUrl } from '../utils/getUploadUrl';

const fields = [
  { name: 'denomination', label: 'Dénomination' },
  { name: 'adresse', label: 'Adresse' },
  { name: 'telfix1', label: 'Tel Fixe 1' },
  { name: 'telfix2', label: 'Tel Fixe 2' },
  { name: 'telmob1', label: 'Tel Mobile 1' },
  { name: 'telmob2', label: 'Tel Mobile 2' },
  { name: 'email', label: 'Email' },
  { name: 'siteweb', label: 'Site web' },
  { name: 'bic', label: 'BIC' },
  { name: 'ncp', label: 'N° Compte' },
  { name: 'logo', label: 'Logo (URL)' },
  { name: 'civilite_responsable', label: 'Civilité Responsable' },
  { name: 'nom_responsable', label: 'Nom Responsable' },
  { name: 'prenom_responsable', label: 'Prénom Responsable' },
  { name: 'titre_responsable', label: 'Titre Responsable' },
  { name: 'ville', label: 'Ville' },
];

const emptyForm = Object.fromEntries(fields.map(f => [f.name, '']));

/** Aperçu d'un fichier File avec URL.createObjectURL et nettoyage mémoire */
const LogoFilePreview = ({ file, className }) => {
  const objectUrl = useMemo(() => (file instanceof File ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);
  if (!objectUrl) return null;
  return <img src={objectUrl} alt="Aperçu du logo" className={className} />;
};

const EtablissementPage = () => {
 // const { user } = useAuth();
  const [etab, setEtab] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const fetchEtab = async () => {
    setLoading(true);
    try {
      const res = await api.get('/etablissement');
      setEtab(res.data.data || null);
    } catch {
      setEtab(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEtab();
  }, []);


  const handleEdit = () => {
    if (etab) {
      // Conserver le logo existant comme string (nom du fichier), pas comme File
      setForm({ ...etab, logo: etab.logo || null });
    } else {
      setForm(emptyForm);
    }
    setModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      // Vérifier s'il y a un fichier logo à uploader
      const hasLogoFile = form.logo instanceof File;
      
      if (hasLogoFile) {
        // Si upload de logo : utiliser FormData
        const formData = new FormData();
        fields.forEach(field => {
          if (field.name === 'logo') {
            // Ajouter le fichier logo uniquement si c'est un File
            if (form.logo instanceof File) {
              formData.append('logo', form.logo);
            }
            // Si ce n'est pas un File mais qu'il y a un logo existant, ne rien faire
            // Le backend conservera le logo existant automatiquement
          } else {
            const value = form[field.name];
            formData.append(field.name, value !== null && value !== undefined ? value : '');
          }
        });
        
        // Ne PAS définir Content-Type explicitement - axios le gère automatiquement avec la boundary
        // Cela permet à axios d'ajouter automatiquement la boundary dans le Content-Type
        try {
          if (etab && etab.id) {
            await api.put(`/etablissement/${etab.id}`, formData);
          } else {
            await api.post('/etablissement', formData);
          }
        } catch (uploadError) {
          console.error('Erreur upload:', uploadError);
          throw uploadError;
        }
      } else {
        // Pas de nouveau logo : utiliser JSON
        // Si on modifie et qu'il y a un logo existant, le conserver
        const data = {};
        fields.forEach(field => {
          if (field.name !== 'logo') {
            const value = form[field.name];
            data[field.name] = value !== null && value !== undefined ? value : '';
          }
        });
        
        // Si on modifie et qu'il y a un logo existant, le conserver
        if (etab && etab.id && etab.logo && !(form.logo instanceof File)) {
          data.logo = etab.logo;
        }
        
        if (etab && etab.id) {
          await api.put(`/etablissement/${etab.id}`, data);
        } else {
          await api.post('/etablissement', data);
        }
      }
      
      setModalOpen(false);
      fetchEtab();
      Swal.fire('Succès', "Les informations de l'établissement ont été enregistrées.", 'success');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      console.error('Error response:', error.response?.data);
      Swal.fire('Erreur', error.response?.data?.message || "Erreur lors de l'enregistrement.", 'error');
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold dark:text-gray-200">Informations de l'établissement</h1>
        <ConditionalButton
          entity="etablissement"
          permission={etab ? "update" : "create"}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-2"
          onClick={handleEdit}
        >
          {etab ? <><FiEdit /> Modifier</> : <><FiPlus /> Ajouter</>}
        </ConditionalButton>
      </div>
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
        {etab ? (
          <>
            {/* Logo en premier, centré et rond */}
            <div className="flex justify-center mb-8">
              <div className="text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Logo</div>
                {etab.logo ? (
                  <div className="flex justify-center">
                    <img 
                      key={etab.logo}
                      src={getUploadUrl(etab.logo, { cacheBust: true })}
                      alt="Logo de l'établissement"
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700 shadow-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                    <div className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 border-4 border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm" style={{ display: 'none' }}>
                      Pas d'image
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 border-4 border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm mx-auto">
                    Aucun logo
                  </div>
                )}
              </div>
            </div>
            
            {/* Autres champs en grille */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.filter(f => f.name !== 'logo').map(f => (
                <div key={f.name} className="mb-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400">{f.label}</div>
                  <div className="font-medium dark:text-gray-200">{etab[f.name] || <span className="text-gray-300 dark:text-gray-600">-</span>}</div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-gray-500 dark:text-gray-400 italic">Aucune information enregistrée.</div>
        )}
      </div>
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          // Réinitialiser le formulaire à la fermeture
          if (etab) {
            setForm({ ...etab, logo: etab.logo || null });
          } else {
            setForm(emptyForm);
          }
        }}
        title={`${etab ? 'Modifier' : 'Ajouter'} les informations de l'établissement`}
        maxWidth="max-w-6xl"
        headerColor="bg-primary-600"
        footer={
          <div className="flex justify-end gap-2">
            <button 
              type="button" 
              onClick={() => {
                setModalOpen(false);
                // Réinitialiser le formulaire à la fermeture
                if (etab) {
                  setForm({ ...etab, logo: etab.logo || null });
                } else {
                  setForm(emptyForm);
                }
              }} 
              className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Annuler
            </button>
            <ConditionalButton
              entity="etablissement"
              permission={etab ? "update" : "create"}
              onClick={() => {
                const form = document.getElementById('etablissement-form');
                if (form) {
                  const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
                  form.dispatchEvent(submitEvent);
                }
              }}
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              Enregistrer
            </ConditionalButton>
          </div>
        }
      >
        <form id="etablissement-form" onSubmit={handleModalSubmit}>
          {/* Logo en premier, centré */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-center dark:text-gray-200">Logo</label>
            <div className="flex flex-col items-center">
                  {form.logo && form.logo instanceof File ? (
                    <LogoFilePreview file={form.logo} className="w-24 h-24 rounded-full object-cover border-3 border-gray-200 shadow-md mb-2" />
                  ) : etab?.logo ? (
                    <img 
                      key={etab.logo}
                      src={getUploadUrl(etab.logo, { cacheBust: true })}
                      alt="Logo actuel"
                      className="w-24 h-24 rounded-full object-cover border-3 border-gray-200 shadow-md mb-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 border-3 border-gray-200 shadow-md flex items-center justify-center text-gray-400 text-xs mb-2">
                      Aucun logo
                    </div>
                  )}
            <input
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files[0];
                if (file) {
                  // Vérifier la taille du fichier (max 5MB)
                  if (file.size > 5 * 1024 * 1024) {
                    Swal.fire('Erreur', 'Le fichier est trop volumineux. Taille maximale: 5MB', 'error');
                    e.target.value = ''; // Réinitialiser l'input
                    return;
                  }
                  // Vérifier le type de fichier
                  if (!file.type.startsWith('image/')) {
                    Swal.fire('Erreur', 'Le fichier doit être une image', 'error');
                    e.target.value = ''; // Réinitialiser l'input
                    return;
                  }
                  setForm({ ...form, logo: file });
                } else {
                  // Si aucun fichier sélectionné, conserver le logo existant (comme string)
                  setForm({ ...form, logo: etab?.logo || null });
                }
              }}
              className="w-full max-w-xs border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Formats acceptés: JPG, PNG, SVG, GIF, WEBP</p>
          </div>
        </div>
        
        {/* Champs organisés selon les spécifications */}
        <div className="space-y-3">
          {/* Dénomination et Adresse sur une ligne */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Dénomination</label>
              <input
                type="text"
                name="denomination"
                value={form.denomination || ''}
                onChange={e => setForm({ ...form, denomination: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Adresse</label>
              <input
                type="text"
                name="adresse"
                value={form.adresse || ''}
                onChange={e => setForm({ ...form, adresse: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Téléphones sur une ligne (4 colonnes) */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Tel Fixe 1</label>
              <input
                type="text"
                name="telfix1"
                value={form.telfix1 || ''}
                onChange={e => setForm({ ...form, telfix1: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Tel Fixe 2</label>
              <input
                type="text"
                name="telfix2"
                value={form.telfix2 || ''}
                onChange={e => setForm({ ...form, telfix2: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Tel Mobile 1</label>
              <input
                type="text"
                name="telmob1"
                value={form.telmob1 || ''}
                onChange={e => setForm({ ...form, telmob1: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Tel Mobile 2</label>
              <input
                type="text"
                name="telmob2"
                value={form.telmob2 || ''}
                onChange={e => setForm({ ...form, telmob2: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Email et Site web sur une ligne */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Email</label>
              <input
                type="text"
                name="email"
                value={form.email || ''}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Site web</label>
              <input
                type="text"
                name="siteweb"
                value={form.siteweb || ''}
                onChange={e => setForm({ ...form, siteweb: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* BIC et N° Compte sur une ligne */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">BIC</label>
              <input
                type="text"
                name="bic"
                value={form.bic || ''}
                onChange={e => setForm({ ...form, bic: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">N° Compte</label>
              <input
                type="text"
                name="ncp"
                value={form.ncp || ''}
                onChange={e => setForm({ ...form, ncp: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Civilité Responsable et Titre Responsable sur une ligne */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Civilité Responsable</label>
              <input
                type="text"
                name="civilite_responsable"
                value={form.civilite_responsable || ''}
                onChange={e => setForm({ ...form, civilite_responsable: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Titre Responsable</label>
              <input
                type="text"
                name="titre_responsable"
                value={form.titre_responsable || ''}
                onChange={e => setForm({ ...form, titre_responsable: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Nom Responsable et Prénom Responsable sur une ligne */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Nom Responsable</label>
              <input
                type="text"
                name="nom_responsable"
                value={form.nom_responsable || ''}
                onChange={e => setForm({ ...form, nom_responsable: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Prénom Responsable</label>
              <input
                type="text"
                name="prenom_responsable"
                value={form.prenom_responsable || ''}
                onChange={e => setForm({ ...form, prenom_responsable: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Ville sur une ligne (pleine largeur) */}
          <div>
            <label className="block text-xs font-medium mb-0.5 dark:text-gray-200">Ville</label>
            <input
              type="text"
              name="ville"
              value={form.ville || ''}
              onChange={e => setForm({ ...form, ville: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
            />
          </div>
        </div>
        </form>
      </Modal>
    </div>
  );
};

export default EtablissementPage;
